import {Box, HStack, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {parseDate} from 'shared/web';

import {Attributes} from '../../components/Attributes/Attributes';
import {CommentMechanic} from '../../components/CommentMechanic/CommentMechanic';
import {ListFeatures} from '../../components/ListFeatures/ListFeatures';
import {SectionPrint} from '../../components/SectionPrint/SectionPrint';
import {useGetDigitalCertificateData} from '../../hooks/useGetDigitalCertificateData';
import {i18n} from '../../i18n/i18n';
import {getTestDrive} from '../../utils/getTestDrive';
import {isCebiaWorkspace} from '../../utils/isCebiaWorkspace';
import {TestDriveContent} from './components/TestDriveContent';
import {hasTestDriveDetailedData} from './utils/hasTestDriveDetailedData';

export function TestDrivePrint() {
  const formatDateTime = useDateTimeFormatter();

  const {vehicleAudit} = useGetDigitalCertificateData();

  const testDrive = getTestDrive(vehicleAudit);

  if (!testDrive) {
    return null;
  }

  const wasTestDrivePerformed =
    testDrive.performed === 'true' || Boolean(testDrive.title.testDriveDate);
  const heading = wasTestDrivePerformed
    ? i18n.t('testDrivePerformed')
    : i18n.t('testDriveNotPerformed');
  const variant = wasTestDrivePerformed ? 'ready' : 'neutral';
  const description = wasTestDrivePerformed ? null : i18n.t('testDriveNotPerformedDescription');

  const hasDetailedData = hasTestDriveDetailedData(testDrive);
  const showMechanicHeader =
    hasDetailedData &&
    Boolean(testDrive.mechanic.name) &&
    isNotNilOrEmpty(testDrive.mechanic.comment);

  if (isCebiaWorkspace() || !hasDetailedData) {
    return (
      <SectionPrint icon="testDrive" heading={i18n.t('testDriveHeader')} header={null}>
        <ListFeatures
          columns={[1, 2, 2, 2]}
          spacing={[6, 6, 10, 10]}
          features={[
            {
              title: heading,
              variant,
              flagTexts: [],
              value: testDrive.mechanic.comment,
              description,
            },
          ]}
        />
      </SectionPrint>
    );
  }

  return (
    <SectionPrint
      icon="testDrive"
      heading={i18n.t('testDriveHeader')}
      header={
        // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
        showMechanicHeader ? (
          <CommentMechanic comment={testDrive.mechanic.comment} name={testDrive.mechanic.name} />
        ) : null
      }
    >
      <VStack spacing={6}>
        <ListFeatures
          columns={[1, 2, 2, 2]}
          spacing={[6, 6, 10, 10]}
          features={[
            {
              title: heading,
              variant,
              flagTexts: [],
              value: showMechanicHeader ? null : testDrive.mechanic.comment,
              description,
            },
          ]}
        />
        <HStack spacing={6} width="100%">
          <Box flex={1} maxWidth={100} width="100%">
            <Attributes
              data-testid="test-Drive-Attributes"
              rows={[
                {
                  label: i18n.t('testDrive.date'),
                  text: testDrive.title.testDriveDate
                    ? formatDateTime('dateShort', parseDate(testDrive.title.testDriveDate))
                    : null,
                },
                {label: i18n.t('testDrive.driver'), text: testDrive.title.driver},
              ]}
            />
          </Box>
          <Box flex={1} width="100%">
            <TestDriveContent
              duration={testDrive.content.duration}
              driveStart={testDrive.content.driveStart}
              driveEnd={testDrive.content.driveEnd}
              distance={testDrive.content.distance}
              maxSpeed={testDrive.content.maxSpeed}
              avgSpeed={testDrive.content.avgSpeed}
              weakGpsSignal={testDrive.content.weakGpsSignal}
            />
          </Box>
        </HStack>
      </VStack>
    </SectionPrint>
  );
}
